import jstz from "jstz";
import { SagaIterator } from "redux-saga";
import { all, call, put, select, take, takeEvery } from "redux-saga/effects";

import { redirectToAccounts } from "helpers/routing";
import { ApiManager } from "services/api";
import { deserializeTimezone } from "services/api/timezone";
import { ITimezone } from "services/api/timezone/timezone-serializer";
import { CRUDAction, RequestAction } from "store/entities/actions";
import { AGENT, selectIsAgentsFetching, selectLoggedInAgent } from "store/entities/agents";
import { IActionWithPayload } from "store/helper";

import { SessionActionNames, SessionActions } from "./actions";
import { IFetchCurrentAgentSuccessPayload } from "./interfaces";
import { selectIsGhostLogin } from "./selectors";

interface IApplication {
  name: string;
  client_id: string;
  session_id: string;
}
interface ISession {
  account_id: string;
  session_id: string;
  current: boolean;
  user_agent: string;
  identity_source: string;
  ip: string;
  applications: IApplication[];
}

function* fetchCurrentAgent(): SagaIterator {
  const [globalAccountsResponse, globalAccountsUserResponse, currentAgent] = yield all([
    call(ApiManager.globalAccountsApi.fetchInfo),
    call(ApiManager.globalAccountsApi.fetchAccount),
    call(ApiManager.agentApi.fetchCurrentAgent),
  ]);

  if (globalAccountsUserResponse.result) {
    yield put(
      SessionActions.fetchCurrentAgentSuccess({
        currentAgentId: currentAgent.result?.ID,
        ...globalAccountsUserResponse.result,
        isGhostLogin: globalAccountsResponse?.result?.ghost !== undefined,
        properties: globalAccountsUserResponse?.result?.properties,
      })
    );
  }
}

function* fetchCurrentAgentSuccess(action: IActionWithPayload<string, IFetchCurrentAgentSuccessPayload>): SagaIterator {
  const { currentAgentId, isGhostLogin, properties } = action.payload;
  yield put(SessionActions.saveCurrentAgent({ id: currentAgentId, isGhostLogin, properties: properties || {} }));
}

function* logout(): SagaIterator {
  const response = yield call(ApiManager.globalAccountsApi.fetchSessions);
  const isGhostLogin = yield select(selectIsGhostLogin);

  if (response.result) {
    if (!isGhostLogin) {
      const sessions: ISession[] = response.result;
      const currentSession = sessions.find(session => session.current === true);
      yield call(ApiManager.globalAccountsApi.deleteSession, currentSession!.session_id);
    } else {
      yield call(ApiManager.globalAccountsApi.revokeToken);
    }
  }
  redirectToAccounts({ signout: true });
}

function* fixAgentTimezone(): SagaIterator {
  const areAgentsFetching = yield select(selectIsAgentsFetching);

  if (areAgentsFetching) {
    yield take([
      AGENT[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS],
      AGENT[CRUDAction.FETCH_COLLECTION][RequestAction.SUCCESS],
    ]);
  }

  const currentAgent = yield select(selectLoggedInAgent);
  if (currentAgent && !currentAgent.settings.timeZone) {
    const guessedTimeZone = yield call(jstz.determine);
    const guessedTimeZoneName = guessedTimeZone.name();

    const response = yield call(ApiManager.timezoneApi.fetch);

    if (response.result) {
      const timezonesList = response.result.map(deserializeTimezone);
      const timezoneFound = timezonesList.some((tz: ITimezone) => tz.id === guessedTimeZoneName);

      if (timezoneFound) {
        yield call(ApiManager.agentApi.updateAgent, currentAgent.id, {
          settings: { timeZone: guessedTimeZoneName },
        });
      }
    }
  }
}

export default function* sessionSagas(): SagaIterator {
  yield all([
    takeEvery(SessionActionNames.FETCH_CURRENT_AGENT, fetchCurrentAgent),
    takeEvery(SessionActionNames.FETCH_CURRENT_AGENT_SUCCESS, fetchCurrentAgentSuccess),
    takeEvery(SessionActionNames.LOGOUT, logout),
    takeEvery(SessionActionNames.SAVE_CURRENT_AGENT, fixAgentTimezone),
  ]);
}
